import { ISharedSitePermit } from '~/client/graph'

import { getAttachmentFileType } from '../components/WorkflowCard/fileAttachmentHelpers'
import {
  WorkflowGroupingKey,
  WorkflowSortingKey,
} from '../components/WorkflowList/WorkflowList.types'
import { LogisticsFilterType } from '../enums/LogisticsFilterType'
import LogisticsGroupingOption from '../enums/LogisticsGroupingOption'
import LogisticsListKeys from '../enums/LogisticsListKeys'
import ILogisticItem, {
  ILogisticItemWorkflow,
  LogisticItemApp,
} from '../models/ILogisticItem'
import LocationBase from '../models/LocationObjects/LocationBase'
import Workflow from '../models/Workflow'
import LocationAttributesStore from '../stores/domain/LocationAttributes.store'
import { LOCATION_SEPARATOR, NO_VALUE } from './usefulStrings'
import { UNASSIGNED } from './ZoneLevelLocationConstants'

export const typeNameToSortIndexMap = {
  Closure: 1,
}

export const isLogisticWorkflow = (
  item: ILogisticItem,
): item is ILogisticItemWorkflow => {
  return item.app === LogisticItemApp.FORM
}

export function extractWorkflowPermits(
  filteredCollection: ILogisticItem[],
): Workflow[] {
  return filteredCollection
    .filter(isLogisticWorkflow)
    .map(item => item.original)
    .filter((permit): permit is Workflow => !!permit)
}

export const getLocationValues = (
  locationsInput: LocationBase[] | string[],
  locationAttributesStore?: LocationAttributesStore,
): string[] => {
  const locations = locationsInput.map(l => {
    if (typeof l === 'string') {
      return locationAttributesStore?.getById(l)
    }
    return l
  })

  return locations
    .filter(l => l)
    .map(l => `${l.type}${LOCATION_SEPARATOR}${l.id}`)
}

export const getIdFromLocation = (location: string): string => {
  return location.split(LOCATION_SEPARATOR)[1]
}

const LOCATION_KEYED_FILTER_TYPES: ReadonlySet<string> = new Set<string>([
  LogisticsFilterType.Location,
  LogisticsFilterType.Equipment,
])

/**
 * Whether a Logistics or workflows filter type keys its options as `Kind|id`. The same
 * two values in LogisticsFilterType, WorkflowsFilterType and UploadLogFilterType.
 */
export const isLocationKeyedFilterType = (filterType: string): boolean =>
  LOCATION_KEYED_FILTER_TYPES.has(filterType)

/**
 * The id half of a `Kind|id` option key, or the key itself when it has no separator
 * (UNASSIGNED, or a bare id).
 *
 * The kind half is a snapshot of the location's kind when the key was written and goes
 * stale when the location is re-kinded (STX1-597), so a key is matched on its id alone.
 */
const locationKeyId = (key: string): string =>
  key.includes(LOCATION_SEPARATOR) ? getIdFromLocation(key) : key

/**
 * The selected options of one filter, for testing the values that filter's
 * createFilterValueByType getter returns. A location-keyed filter compares by id: `has`
 * takes a `Kind|id` key or a bare id, and matches whatever kind either side was written
 * with. Any other filter gets the selection itself.
 *
 * Build it once per filter, not per item: for a location-keyed filter it copies the keys.
 */
export function getSelectedFilterValues(
  filterType: string,
  initialFilterOptions: Map<string, string[]>,
): { has(value: string): boolean } {
  if (!isLocationKeyedFilterType(filterType)) return initialFilterOptions

  const selectedIds = new Set(
    Array.from(initialFilterOptions.keys(), locationKeyId),
  )

  return { has: value => !!value && selectedIds.has(locationKeyId(value)) }
}

/**
 * Saved `Kind|id` keys re-pointed at the live option key with the same id, so a saved view
 * still selects a location after it is re-kinded: the options carry the live kind, the
 * saved view the kind it had when it was saved. A key with no live match - UNASSIGNED, a
 * deleted location - is returned unchanged; a key that is already live maps to itself.
 * Order is kept and duplicates are dropped.
 */
export function matchLocationKeysToOptions(
  savedKeys: string[],
  optionKeys: string[],
): string[] {
  const liveKeys = new Set(optionKeys)
  const optionKeyById = new Map<string, string>()
  optionKeys.forEach(optionKey => {
    const id = locationKeyId(optionKey)
    if (!optionKeyById.has(id)) optionKeyById.set(id, optionKey)
  })

  return Array.from(
    new Set(
      savedKeys.map(savedKey =>
        liveKeys.has(savedKey)
          ? savedKey
          : (optionKeyById.get(locationKeyId(savedKey)) ?? savedKey),
      ),
    ),
  )
}

/** One `Kind|id` location filter key as a report summary lists it. */
export interface ILocationFilterKeySummary {
  type: string
  name: string
}

/**
 * The group and name a report summary lists one `Kind|id` location filter key under, both
 * read off the live location by id: the kind half is the kind the key was written with,
 * which a re-kind changes and a project-added kind has no store of (STX1-597). The kind
 * half groups only a key whose location this client does not hold.
 */
export function getLocationFilterKeySummary(
  key: string,
  getLocationById: (
    id: string,
  ) => Pick<LocationBase, 'type' | 'name'> | undefined,
): ILocationFilterKeySummary {
  const [keyType, id] = key.split(LOCATION_SEPARATOR)
  const location = getLocationById(id)

  return { type: location?.type || keyType, name: location?.name || NO_VALUE }
}

export function createFilterValueByType(
  getSharedFormById: (id: string) => ISharedSitePermit | undefined,
  getActiveProjectId: () => string,
): Record<LogisticsFilterType, (inst: ILogisticItem) => string[]> {
  // Locations and equipment by id alone, compared through getSelectedFilterValues (STX1-597)
  return {
    [LogisticsFilterType.Location]: (inst: ILogisticItem) =>
      inst.locations.map(l => l.id),
    [LogisticsFilterType.Type]: (inst: ILogisticItem) => [
      inst.type || UNASSIGNED,
    ],
    [LogisticsFilterType.Status]: (inst: ILogisticItem) => [inst.status],
    [LogisticsFilterType.Company]: (inst: ILogisticItem) => inst.companyIds,
    [LogisticsFilterType.Equipment]: (inst: ILogisticItem) =>
      inst.equipment.map(e => e.id),
    [LogisticsFilterType.ResponsibleContact]: (inst: ILogisticItem) =>
      inst.responsibleContactIds,
    [LogisticsFilterType.Category]: (inst: ILogisticItem) => [
      inst.formCategoryId || UNASSIGNED,
    ],
    [LogisticsFilterType.Project]: (inst: ILogisticItem) => [
      getSharedFormById(inst.id)?.projectFromId || getActiveProjectId(),
    ],
    [LogisticsFilterType.Attachment]: (inst: ILogisticItem) =>
      Array.from(
        new Set(
          (inst.attachments || []).map(a => getAttachmentFileType(a.fileName)),
        ),
      ),
  }
}

const LOGISTICS_TO_WORKFLOW_GROUPING: Partial<
  Record<string, WorkflowGroupingKey>
> = {
  [LogisticsGroupingOption.TYPE]: WorkflowGroupingKey.TYPE,
  [LogisticsGroupingOption.STATUS]: WorkflowGroupingKey.STATUS,
  [LogisticsGroupingOption.CATEGORY]: WorkflowGroupingKey.CATEGORY,
  [LogisticsGroupingOption.DATE]: WorkflowGroupingKey.DATE,
  [LogisticsGroupingOption.DATE_TIME]: WorkflowGroupingKey.TIME,
  [LogisticsGroupingOption.COMPANY]: WorkflowGroupingKey.COMPANY,
  [LogisticsGroupingOption.RESPONSIBLE]: WorkflowGroupingKey.CONTACT,
  [LogisticsGroupingOption.LBS]: WorkflowGroupingKey.LOCATION,
  [LogisticsGroupingOption.EQUIPMENT]: WorkflowGroupingKey.EQUIPMENT,
  [LogisticsGroupingOption.FILE_TYPE]: WorkflowGroupingKey.FILE_TYPE,
  [LogisticsGroupingOption.PROJECT]: WorkflowGroupingKey.PROJECT,
}

export function toWorkflowGroupingKey(
  logisticsKey: string | undefined,
): WorkflowGroupingKey | null {
  if (!logisticsKey || logisticsKey === LogisticsGroupingOption.NONE) {
    return null
  }
  return LOGISTICS_TO_WORKFLOW_GROUPING[logisticsKey] ?? null
}

const LOGISTICS_TO_WORKFLOW_SORTING: Partial<
  Record<string, WorkflowSortingKey>
> = {
  [LogisticsListKeys.LOGISTIC_NAME]: WorkflowSortingKey.NAME,
  [LogisticsListKeys.LOGISTIC_TYPE]: WorkflowSortingKey.TYPE,
  [LogisticsListKeys.LOGISTIC_CATEGORY]: WorkflowSortingKey.CATEGORY,
  [LogisticsListKeys.LOGISTIC_STATUS]: WorkflowSortingKey.STATUS,
  [LogisticsListKeys.LOGISTIC_LOCATION]: WorkflowSortingKey.LOCATION,
  [LogisticsListKeys.LOGISTIC_EQUIPMENT]: WorkflowSortingKey.EQUIPMENT,
  [LogisticsListKeys.LOGISTIC_COMPANY]: WorkflowSortingKey.COMPANY,
  [LogisticsListKeys.LOGISTIC_DATE]: WorkflowSortingKey.DATE,
  [LogisticsListKeys.LOGISTIC_START_DATE]: WorkflowSortingKey.DATE,
  [LogisticsListKeys.LOGISTIC_RESPONSIBLE]: WorkflowSortingKey.CONTACT,
  [LogisticsListKeys.LOGISTIC_ATTACHMENT]: WorkflowSortingKey.FILE_TYPE,
}

export function toWorkflowSortingKey(
  logisticsKey: string | null | undefined,
): WorkflowSortingKey | null {
  if (!logisticsKey) return null
  return LOGISTICS_TO_WORKFLOW_SORTING[logisticsKey] ?? null
}
